import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { computeBannerOffsetPx } from '../lib/pendingOAuth.js';

function formatExpiry(value, language) {
  const timestamp = Date.parse(value || '');
  if (!Number.isFinite(timestamp)) return '';
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(timestamp));
  } catch {
    return '';
  }
}

// Owner bug: this banner is `position: fixed` (see .is-floating in
// pending-oauth.css) so it renders ON TOP of whatever screen is mounted
// beneath it. On /onboarding that hid the header's Skip button entirely on
// mobile. The non-/app `floating` instance publishes its live rendered
// bottom edge here so any screen with its own fixed/sticky header can add
// this as a top offset instead of being covered — see .ob-top / .ob-corner-
// actions in styles/onboarding.css. The /app instance is excluded because it
// lives in AppShell's normal flow below the topbar and needs no measured offset.
const OFFSET_VAR = '--pending-oauth-banner-h';

export default function PendingOAuthBanner({ floating = false, appRoute = false }) {
  const { t, i18n } = useTranslation();
  const {
    pending,
    billingReal,
    bannerDismissed,
    consentNavigationStatus,
    manualConsentAvailable,
    restartConsentRequired,
    resume,
    dismissBanner,
    personalPendingOptions = [],
    personalStartStatus = 'idle',
    personalAutomaticTransition = false,
    continuePersonal,
  } = usePendingOAuth();
  const rootRef = useRef(null);

  const quietReturn = billingReal && manualConsentAvailable;
  const personalContinuation = personalPendingOptions.length > 0 || personalStartStatus === 'changed';
  const hidden = !personalContinuation && (!pending
    || (billingReal && !quietReturn && !restartConsentRequired)
    || (bannerDismissed && (!billingReal || quietReturn)));

  // useLayoutEffect (not useEffect) so the offset is published before the
  // browser paints — otherwise the header would flash uncovered for one
  // frame, then jump down once the effect ran.
  useLayoutEffect(() => {
    if (!floating || appRoute || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const el = rootRef.current;
    if (hidden || !el) {
      root.style.setProperty(OFFSET_VAR, '0px');
      return undefined;
    }
    const publish = () => {
      root.style.setProperty(OFFSET_VAR, `${computeBannerOffsetPx(el.getBoundingClientRect())}px`);
    };
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    observer?.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      root.style.setProperty(OFFSET_VAR, '0px');
    };
  }, [floating, appRoute, hidden]);

  if (hidden) return null;
  const expiry = formatExpiry(pending?.expiresAt, i18n.language);

  return (
    <div ref={rootRef} className={`pending-oauth-banner${floating ? ' is-floating' : ''}${appRoute ? ' is-app-route' : ''}${quietReturn ? ' is-quiet' : ''}${personalContinuation ? ' is-personal' : ''}`} role="status" aria-live="polite">
      {personalAutomaticTransition ? (
        <strong>{t('freePublishing.preparing')}</strong>
      ) : personalContinuation ? (
        <>
          <div className="pending-oauth-banner__copy">
            <strong>{t('freePublishing.connectTitle')}</strong>
            <span>{t('freePublishing.connectHint')}</span>
            {['failed', 'changed'].includes(personalStartStatus) && <span role="alert">{t(`freePublishing.${personalStartStatus === 'changed' ? 'connectionChanged' : 'startError'}`)}</span>}
          </div>
          <div className="pending-oauth-banner__actions">
            {personalPendingOptions.map(option => (
              <button key={option.connector} type="button" className="btn btn-primary"
                disabled={['saving', 'returning'].includes(personalStartStatus)} onClick={() => continuePersonal(option.connector)}>
                {t(personalStartStatus === 'saving' ? 'freePublishing.preparing' : 'freePublishing.continueConnector',
                  { connector: option.connector === 'claude' ? 'Claude' : 'ChatGPT' })}
              </button>
            ))}
          </div>
        </>
      ) : restartConsentRequired ? (
        <strong>{t('pendingOAuth.restartRequired')}</strong>
      ) : quietReturn ? (
        <>
          <div className="pending-oauth-banner__copy">
            <strong>{t('pendingOAuth.finishTitle')}</strong>
            <span>{t('pendingOAuth.finishDescription')}</span>
          </div>
          <div className="pending-oauth-banner__actions">
            <button type="button" className="pending-oauth-banner__return" onClick={resume}>
              {t(consentNavigationStatus === 'attempted'
                ? 'pendingOAuth.continueConnection'
                : 'pendingOAuth.reviewPermissions')}
            </button>
            <button type="button" className="pending-oauth-banner__close" onClick={dismissBanner} aria-label={t('pendingOAuth.hide')}>
              ×
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="pending-oauth-banner__copy">
            <strong>{t('pendingOAuth.waiting')}</strong>
            {expiry && <span>{t('pendingOAuth.expiresAt', { time: expiry })}</span>}
          </div>
          <button type="button" className="pending-oauth-banner__close" onClick={dismissBanner} aria-label={t('pendingOAuth.hide')}>
            ×
          </button>
        </>
      )}
    </div>
  );
}
